import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MoveUpIcon from "@mui/icons-material/MoveUp";
import PlaceIcon from "@mui/icons-material/Place";
import IconButton from "@mui/material/IconButton";

import { APP_INVENTORY_API_BASE } from "../../../../config/api";
const API_BASE = `${APP_INVENTORY_API_BASE}`;

const MOVEMENT_TYPES = [
  { value: "DISPATCH", label: "Dispatch" },
  { value: "DELIVERY", label: "Delivery" },
  { value: "RETURN", label: "Return" },
  { value: "TRANSFER", label: "Transfer" },
  { value: "REDEPLOYMENT", label: "Redeployment" },
];

const MOVEMENT_STATUSES = [
  { value: "COMPLETED", label: "Completed" },
  { value: "PENDING", label: "Pending" },
  { value: "IN_TRANSIT", label: "In Transit" },
];

export default function BulkMovementModal({
  open,
  onClose,
  onSuccess,
  assets,
  token,
  activeOrg,
  mode = "movement",
}) {
  const isAssignLocation = mode === "assignLocation";
  const [locations, setLocations] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [form, setForm] = useState({
    movement_type: isAssignLocation ? "TRANSFER" : "DISPATCH",
    movement_status: "COMPLETED",
    from_location_id: "",
    to_location_id: "",
    courier_name: "",
    tracking_number: "",
    reference_number: "",
    moved_at: "",
    remarks: "",
  });

  const authHeaders = {
    Authorization: `Bearer ${token}`,
    ...(activeOrg ? { "X-Org-ID": activeOrg.id } : {}),
  };

  useEffect(() => {
    if (!open) return;
    setError("");
    setSubmitting(false);
    setResult(null);
    setForm({
      movement_type: isAssignLocation ? "TRANSFER" : "DISPATCH",
      movement_status: "COMPLETED",
      from_location_id: "",
      to_location_id: "",
      courier_name: "",
      tracking_number: "",
      reference_number: "",
      moved_at: "",
      remarks: "",
    });
  }, [open, isAssignLocation]);

  useEffect(() => {
    if (!open || !activeOrg?.id) return;
    let mounted = true;
    const loadLocations = async () => {
      try {
        setLoadingLocations(true);
        const res = await axios.get(`${API_BASE}/locations/${activeOrg.id}`, {
          headers: authHeaders,
          params: { is_active: "true", per_page: 200 },
        });
        if (mounted) setLocations(res.data?.data || []);
      } catch {
        if (mounted) setLocations([]);
      } finally {
        if (mounted) setLoadingLocations(false);
      }
    };
    loadLocations();
    return () => {
      mounted = false;
    };
  }, [open, activeOrg?.id]);

  const ineligibleAssets = useMemo(
    () =>
      (assets || []).filter(
        (asset) => asset.status !== "IN_STOCK" && !isAssignLocation,
      ),
    [assets, isAssignLocation],
  );

  const handleSubmit = async () => {
    setError("");
    if (!assets?.length) {
      setError("Select at least one asset first.");
      return;
    }
    if (isAssignLocation && !form.to_location_id) {
      setError("Choose a destination location.");
      return;
    }
    if (!isAssignLocation && !form.movement_type) {
      setError("Choose a movement type.");
      return;
    }
    if (!form.from_location_id && !form.to_location_id) {
      setError("Choose at least one location.");
      return;
    }

    setSubmitting(true);
    try {
      const url = isAssignLocation
        ? `${API_BASE}/movements/bulk-assign-location`
        : `${API_BASE}/movements/bulk-log`;
      const payload = {
        asset_ids: assets.map((asset) => asset.id),
        movement_type: form.movement_type,
        movement_status: form.movement_status,
        ...(form.from_location_id && {
          from_location_id: form.from_location_id,
        }),
        ...(form.to_location_id && { to_location_id: form.to_location_id }),
        ...(form.courier_name && { courier_name: form.courier_name.trim() }),
        ...(form.tracking_number && {
          tracking_number: form.tracking_number.trim(),
        }),
        ...(form.reference_number && {
          reference_number: form.reference_number.trim(),
        }),
        ...(form.moved_at && { moved_at: form.moved_at }),
        ...(form.remarks && { remarks: form.remarks.trim() }),
      };
      const res = await axios.post(url, payload, { headers: authHeaders });
      setResult(res.data);
      onSuccess?.(res.data);
    } catch (err) {
      setError(err.response?.data?.error || "Bulk action failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={700}>
            {isAssignLocation ? "Bulk assign location" : "Bulk log movement"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isAssignLocation
              ? "Place selected assets into a destination location while keeping the single-asset movement flow intact."
              : "Record one shared physical movement event for all selected assets."}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5}>
          {error && <Alert severity="error">{error}</Alert>}
          {result && (
            <Alert severity={result.failed ? "warning" : "success"}>
              {result.message} — {result.succeeded} succeeded
              {result.failed ? `, ${result.failed} failed` : ""}.
            </Alert>
          )}
          {ineligibleAssets.length > 0 && (
            <Alert severity="info">
              {ineligibleAssets.length} selected asset
              {ineligibleAssets.length !== 1 ? "s are" : " is"} not in stock.
              Movements can still be logged, but provisioning should only use
              in-stock assets.
            </Alert>
          )}

          <Box display="flex" flexWrap="wrap" gap={1}>
            {(assets || []).slice(0, 8).map((asset) => (
              <Chip
                key={asset.id}
                label={`${asset.serial_number} · ${asset.asset_type || asset.model || "Asset"}`}
                variant="outlined"
              />
            ))}
            {(assets?.length || 0) > 8 && (
              <Chip label={`+${assets.length - 8} more`} variant="outlined" />
            )}
          </Box>

          {!isAssignLocation && (
            <TextField
              select
              label="Movement type"
              size="small"
              value={form.movement_type}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, movement_type: e.target.value }))
              }
            >
              {MOVEMENT_TYPES.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          )}

          <Box
            display="grid"
            gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }}
            gap={2}
          >
            <TextField
              select
              label="From location"
              size="small"
              value={form.from_location_id}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  from_location_id: e.target.value,
                }))
              }
              disabled={loadingLocations}
            >
              <MenuItem value="">External / unknown origin</MenuItem>
              {locations.map((location) => (
                <MenuItem key={location.id} value={location.id}>
                  {location.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="To location"
              size="small"
              value={form.to_location_id}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, to_location_id: e.target.value }))
              }
              disabled={loadingLocations}
            >
              <MenuItem value="">External / no destination</MenuItem>
              {locations.map((location) => (
                <MenuItem key={location.id} value={location.id}>
                  {location.name}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <Box
            display="grid"
            gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }}
            gap={2}
          >
            <TextField
              select
              label="Movement status"
              size="small"
              value={form.movement_status}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  movement_status: e.target.value,
                }))
              }
            >
              {MOVEMENT_STATUSES.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Moved at"
              type="datetime-local"
              size="small"
              value={form.moved_at}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, moved_at: e.target.value }))
              }
              InputLabelProps={{ shrink: true }}
            />
          </Box>

          <Box
            display="grid"
            gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr 1fr" }}
            gap={2}
          >
            <TextField
              label="Reference number"
              size="small"
              value={form.reference_number}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  reference_number: e.target.value,
                }))
              }
            />
            <TextField
              label="Courier name"
              size="small"
              value={form.courier_name}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, courier_name: e.target.value }))
              }
            />
            <TextField
              label="Tracking number"
              size="small"
              value={form.tracking_number}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  tracking_number: e.target.value,
                }))
              }
            />
          </Box>

          <TextField
            label="Remarks"
            size="small"
            multiline
            rows={3}
            value={form.remarks}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, remarks: e.target.value }))
            }
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={onClose}
          color="inherit"
          sx={{ textTransform: "none" }}
        >
          Close
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || loadingLocations}
          startIcon={
            submitting ? (
              <CircularProgress size={14} color="inherit" />
            ) : isAssignLocation ? (
              <PlaceIcon fontSize="small" />
            ) : (
              <MoveUpIcon fontSize="small" />
            )
          }
          sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2 }}
        >
          {submitting
            ? "Submitting..."
            : isAssignLocation
              ? "Assign location"
              : "Log movement"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
